import type { GridSortModel, GridFilterItem } from "@mui/x-data-grid"

import useSWR from "swr"
import { useMemo } from "react"

import { httpClient as axiosInstance, fetcher, apiUrls } from "@/src/api"

import type { MandatesModel } from "../types/mandatesModel"
import type { ClientsModel, ClientTableFilters } from "../types/clientsModel"
import { FormSchemaClientType } from "@/src/schemas/formSchemas"
import { swrOptions } from "./leads"
import { uploadGCSFile } from "./signedGCSFile"
import { ApiResponse } from "../types/apiResponse"

// ----------------------------------------------------------------------

type ClientsData = {
    data: {
        content: ClientsModel[]
        totalElements: number | undefined
        clientCountsByStatus: { [key: string]: number }
    }
}

export function useGetClients(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: ClientTableFilters,
    enabled: boolean = true
) {
    const url = useMemo(() => apiUrls.CLIENTS_LIST(), [])

    const sortQuery = useMemo(() => sortModel?.map((e) => `${e.field},${e.sort}`).join("&"), [sortModel])

    const filters = useMemo(() => {
        let filterString = filterOptions
            .filter((e) => e.value)
            .map((e) => `${e.field}=${e.value}`)
            .join("&")

        if (extraFilters.status?.length) {
            filterString = filterString
                ? `${filterString}&status=${extraFilters.status[0]}`
                : `status=${extraFilters.status[0]}`
        }

        return filterString
    }, [filterOptions, extraFilters.status])

    const swrKey = useMemo(
        () =>
            enabled
                ? `${url}?page=${page}&size=${size}${sortQuery ? `&sort=${sortQuery}` : ""}${search ? `&search=${search}` : ""}${filters ? `&${filters}` : ""}`
                : null,
        [enabled, url, page, size, sortQuery, search, filters]
    )

    const { data, isLoading, error, isValidating, mutate } = useSWR<ClientsData>(swrKey, fetcher, {
        ...swrOptions,
        revalidateOnFocus: false, // Prevent revalidation on window focus
        revalidateOnReconnect: false, // Prevent revalidation on network reconnect
        revalidateIfStale: false, // Only revalidate when explicitly requested
    })

    const memoizedValue = useMemo(
        () => ({
            clients: data?.data?.content || [],
            tabData: data?.data?.clientCountsByStatus,
            clientsCount: data?.data?.totalElements,
            clientsLoading: isLoading,
            clientsError: error,
            clientsValidating: isValidating,
            clientsEmpty: !isLoading && !data?.data?.content?.length,
            clientsMutate: mutate,
        }),
        [data, error, isLoading, isValidating, mutate]
    )

    return memoizedValue
}

// ----------------------------------------------------------------------

type ClientData = {
    data: { client: ClientsModel; mandates: MandatesModel[] }
}

export function useGetClient(clientId: string | undefined) {
    // Memoize URL to prevent unnecessary recalculations
    const url = useMemo(() => {
        return clientId ? apiUrls.CLIENT_DETAILS(clientId) : ""
    }, [clientId])

    const { data, isLoading, error, isValidating } = useSWR<ClientData>(url, fetcher, {
        ...swrOptions,
        revalidateOnFocus: false, // Prevent revalidation on window focus
        revalidateOnReconnect: false, // Prevent revalidation on network reconnect
        revalidateIfStale: false, // Only revalidate when explicitly requested
    })

    const memoizedValue = useMemo(() => {
        return {
            client: data?.data?.client,
            clientMandates: data?.data?.mandates,
            clientLoading: isLoading,
            clientError: error,
            clientValidating: isValidating,
        }
    }, [data, error, isLoading, isValidating])

    return memoizedValue
}

export const updateClientForm = (payload: FormSchemaClientType) => {
    return axiosInstance.post(apiUrls.CLIENT_FORM(), payload)
}

export const editClientForm = (payload: FormSchemaClientType, id: string) => {
    return axiosInstance.put(`${apiUrls.CLIENT_FORM()}/${id}`, payload)
}

export const uploadClientLogo = async (logo: File, client: ClientsModel): Promise<ApiResponse<ClientsModel>> => {
    const resp = await uploadGCSFile(logo, "PUBLIC")
    if (resp.status === "error" || !resp.data) return { status: "error", errorMessage: resp.errorMessage }

    const fileName = resp.data.split("/").pop()
    const newLogoUrl = `https://storage.googleapis.com/mumbai_apnatime_prod/only-round/${fileName}`

    return axiosInstance
        .put(`${apiUrls.CLIENT_FORM()}/${client.id}`, {
            ...client,
            logoUrl: newLogoUrl,
        })
        .then((updateResp): ApiResponse<ClientsModel> => {
            return { status: "success", data: updateResp.data?.data }
        })
        .catch((error): ApiResponse<ClientsModel> => {
            console.log(error)
            return { status: "error", errorMessage: error.message }
        })
}
